/**
 * Centralized display name validation utilities for NewME
 * This ensures consistent display name validation across all components
 */

export interface DisplayNameValidationResult {
  isValid: boolean;
  error?: string;
}

/**
 * Validates a display name according to NewME standards
 * @param displayName - The display name to validate
 * @returns DisplayNameValidationResult with validation status and error message if invalid
 */
export const validateDisplayName = (
  displayName: string
): DisplayNameValidationResult => {
  // Check if display name exists
  if (!displayName || !displayName.trim()) {
    return {
      isValid: false,
      error: "Display name is required",
    };
  }

  const trimmedDisplayName = displayName.trim();

  // Check minimum length
  if (trimmedDisplayName.length < 2) {
    return {
      isValid: false,
      error: "Display name must be at least 2 characters long",
    };
  }

  // Check maximum length
  if (trimmedDisplayName.length > 50) {
    return {
      isValid: false,
      error: "Display name must be 50 characters or less",
    };
  }

  // Display name is valid
  return {
    isValid: true,
  };
};

/**
 * Gets the HTML input attributes for display name fields
 * @returns Object with HTML attributes for display name inputs
 */
export const getDisplayNameInputProps = () => ({
  maxLength: 50,
  minLength: 2,
  title: "Display name must be between 2 and 50 characters",
});
